<template>
	<CToolTip
		class="cog-nav-button__tooltip"
		:use-button="false"
		placement="right"
		:open-delay="1000"
		popper-class="tooltip--nav tooltip--nav-shortcut"
		:disabled="buttonDisabled"
	>
		<CShortcutKeys />
		<template #button>
			<button
				class="cog-nav-button__button button--no-style relative p-3"
				:class="{ animate: !buttonDisabled }"
				aria-controls="side-menu"
				:aria-label="navIsOpen ? resources.CloseSidebar : resources.OpenSidebar"
				:disabled="buttonDisabled"
				@click="handleClick"
				@mouseenter="!isMobile && $emit('show-popper')"
				@mouseleave="!isMobile && $emit('hide-popper')"
			>
				<Inline
					v-if="!isMobile && !logoUrl"
					class="cog-nav-button__icon pin-y-center pin-x-center"
					svg="collapse-menu"
				/>
				<img
					v-if="logoUrl"
					:src="logoUrl"
					alt=""
					class="cog-nav-button__org-logo relative"
					@load="onLogoLoad"
				/>
				<Inline
					v-else
					class="cog-nav-button__cogicon relative"
					svg="logos/cogicon"
				/>
			</button>
		</template>
	</CToolTip>
</template>

<script lang="ts">
	import { defineComponent, inject, computed, ref } from 'vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import { useRouter, useRoute } from 'vue-router/composables';
	import { getFallbackRoute } from 'src/views/router';
	import { isMobileKey, localizationResourcesKey, navStateKey } from 'src/utilities/injection-keys';
	import CShortcutKeys from 'src/components/info/ShortcutKeys.vue';

	export default defineComponent({
		name: 'CogNavButton',
		components: {
			CToolTip,
			CShortcutKeys
		},
		props: {
			isCollapsed: { type: Boolean, required: true },
			logoUrl: { type: String, default: '' }
		},
		emits: ['toggle-open', 'show-popper', 'hide-popper', 'close-popper'],
		setup(props, { emit }) {
			const router = useRouter();
			const route = useRoute();
			const isMobile = inject(isMobileKey);
			const navState = inject(navStateKey);
			const resources = inject(localizationResourcesKey);

			const isPopperOnlyState = computed(() => route.meta.showNavAsPopperOnly);

			const logoLoading = ref(true);

			const buttonDisabled = computed(() => {
				if (isMobile.value)
					return true;

				const isPopperModeNotOverlay = navState.isPopper.value && !navState.showOverlay.value;

				// Only disable if in popper mode and docking is disallowed for current route
				return isPopperModeNotOverlay && isPopperOnlyState.value;
			});

			const handleClick = () => {
				if (!props.isCollapsed && isMobile.value) {
					return;
				}

				if (isPopperOnlyState.value) {
					if (navState.isPopper.value)
						emit('close-popper');
					else
						emit('show-popper', true);
					return;
				}

				if (!isMobile.value) {
					emit('toggle-open');
				}
				else {
					router.push(getFallbackRoute(router));
				}
			};

			const onLogoLoad = (e) => {
				logoLoading.value = false;
			};

			return {
				isMobile,
				resources,
				handleClick,
				buttonDisabled,
				navIsOpen: navState.isOpen,
				logoLoading,
				onLogoLoad
			};
		}
	});
</script>

<style lang="scss">
@keyframes half-spin {

	0% {
		transform: rotate(0deg);
	}

	20% {
		transform: rotate(-20deg);
	}

	100% {
		transform: rotate(180deg);
	}
}

@keyframes bounce {

	0% {
		transform: scale(1);
	}

	10% {
		transform: scale(.8);
	}

	95% {
		transform: scale(1.05);
	}

	100% {
		transform: scale(1);
	}
}

@keyframes shrink-fade {

	0% {
		opacity: 1;
		transform: scale(1);
	}

	100% {
		opacity: 0;
		transform: scale(0);
	}
}

.cog-nav-button {

	.admin--nav-collapsed:not(.admin--nav-overlay) &__icon {
		transform: rotate(.5turn);
	}

	&__org-logo {
		border-radius: 4px;
	}

	&__button {
		width: 48px;
		height: 48px;

		&.animate:hover {

			.i-cogicon__cog {
				transform-origin: center center;
				transform-box: fill-box;
				animation: half-spin .6s;
			}

			.i-cogicon__c {
				transform-origin: center center;
				transform-box: fill-box;
				animation: bounce .4s;
			}

			.nav__side-menu & .i-cogicon {
				animation: shrink-fade .3s .2s forwards;
			}
		}
	}

	&__tooltip {
		-webkit-tap-highlight-color: transparent;
	}
}
</style>
